/**
 * Subscription Notification Service
 * Handles in-app notifications for subscription renewals, expirations, and other subscription events
 */

import { notificationManager } from '@flowstate/core';
import { NOTIFICATION_TYPES } from '@/types/notification';
import { logger } from '@/lib/logger';
import type { SubscriptionRenewalAlert } from '@flowstate/types/accounting';

export interface SubscriptionNotificationOptions {
  userId: string;
  subscriptionId: string;
  vendorName: string;
  amount: number;
  currency: string;
  renewalDate: string;
  daysUntilRenewal: number;
}

class SubscriptionNotificationService {
  /**
   * Send notification for upcoming subscription renewal
   */
  async notifyUpcomingRenewal(options: SubscriptionNotificationOptions) {
    const { userId, subscriptionId, vendorName, amount, currency, renewalDate, daysUntilRenewal } = options;

    try {
      const message = daysUntilRenewal === 0
        ? `Your ${vendorName} subscription renews today (${currency} ${amount.toFixed(2)})`
        : daysUntilRenewal === 1
        ? `Your ${vendorName} subscription renews tomorrow (${currency} ${amount.toFixed(2)})`
        : `Your ${vendorName} subscription renews in ${daysUntilRenewal} days (${currency} ${amount.toFixed(2)})`;

      const result = await notificationManager.createNotification({
        user_id: userId,
        type: NOTIFICATION_TYPES.SUBSCRIPTION_RENEWAL_DUE,
        title: 'Subscription renewal reminder',
        message_preview: message,
        data: {
          subscriptionId,
          vendorName,
          amount,
          currency,
          renewalDate,
          daysUntilRenewal,
        },
        entity_type: 'subscription',
        entity_id: subscriptionId,
      });

      if (result.error) {
        logger.error('Failed to create renewal notification', {
          error: result.error,
          subscriptionId,
          userId,
        });
        return { success: false, error: result.error };
      }

      logger.info('Created renewal notification', {
        subscriptionId,
        userId,
        daysUntilRenewal,
      });

      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error creating renewal notification', {
        error,
        subscriptionId,
        userId,
      });
      return { success: false, error: String(error) };
    }
  }

  /**
   * Send notification for expired subscription
   */
  async notifyExpiredSubscription(
    userId: string,
    subscriptionId: string,
    vendorName: string
  ) {
    try {
      const message = `Your ${vendorName} subscription has expired`;

      const result = await notificationManager.createNotification({
        user_id: userId,
        type: NOTIFICATION_TYPES.SUBSCRIPTION_EXPIRED,
        title: 'Subscription expired',
        message_preview: message,
        data: {
          subscriptionId,
          vendorName,
        },
        entity_type: 'subscription',
        entity_id: subscriptionId,
      });

      if (result.error) {
        logger.error('Failed to create expiration notification', {
          error: result.error,
          subscriptionId,
          userId,
        });
        return { success: false, error: result.error };
      }

      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error creating expiration notification', {
        error,
        subscriptionId,
        userId,
      });
      return { success: false, error: String(error) };
    }
  }

  /**
   * Send notification for payment failure
   */
  async notifyPaymentFailed(
    userId: string,
    subscriptionId: string,
    vendorName: string,
    amount: number,
    currency: string
  ) {
    try {
      const message = `Payment failed for ${vendorName} subscription (${currency} ${amount.toFixed(2)})`;

      const result = await notificationManager.createNotification({
        user_id: userId,
        type: NOTIFICATION_TYPES.SUBSCRIPTION_PAYMENT_FAILED,
        title: 'Subscription payment failed',
        message_preview: message,
        data: {
          subscriptionId,
          vendorName,
          amount,
          currency,
        },
        entity_type: 'subscription',
        entity_id: subscriptionId,
      });

      if (result.error) {
        logger.error('Failed to create payment failure notification', {
          error: result.error,
          subscriptionId,
          userId,
        });
        return { success: false, error: result.error };
      }

      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error creating payment failure notification', {
        error,
        subscriptionId,
        userId,
      });
      return { success: false, error: String(error) };
    }
  }

  /**
   * Send notification for cost increase
   */
  async notifyCostIncrease(
    userId: string,
    subscriptionId: string,
    vendorName: string,
    oldAmount: number,
    newAmount: number,
    currency: string
  ) {
    try {
      const increase = newAmount - oldAmount;
      const percentIncrease = ((increase / oldAmount) * 100).toFixed(1);
      const message = `${vendorName} subscription cost increased by ${currency} ${increase.toFixed(2)} (${percentIncrease}%)`;

      const result = await notificationManager.createNotification({
        user_id: userId,
        type: NOTIFICATION_TYPES.SUBSCRIPTION_COST_INCREASE,
        title: 'Subscription cost increased',
        message_preview: message,
        data: {
          subscriptionId,
          vendorName,
          oldAmount,
          newAmount,
          currency,
          increase,
          percentIncrease,
        },
        entity_type: 'subscription',
        entity_id: subscriptionId,
      });

      if (result.error) {
        logger.error('Failed to create cost increase notification', {
          error: result.error,
          subscriptionId,
          userId,
        });
        return { success: false, error: result.error };
      }

      return { success: true, data: result.data };
    } catch (error) {
      logger.error('Error creating cost increase notification', {
        error,
        subscriptionId,
        userId,
      });
      return { success: false, error: String(error) };
    }
  }

  /**
   * Check and send notifications for upcoming renewals based on renewal alerts
   */
  async processRenewalAlerts(renewalAlerts: SubscriptionRenewalAlert[]) {
    logger.info('Processing renewal alerts', { count: renewalAlerts.length });

    const results = await Promise.allSettled(
      renewalAlerts.map(async (alert) => {
        // Only send notifications for renewals within 7 days
        if (alert.days_until_renewal <= 7) {
          logger.warn('Skipping renewal alert notification without user context', {
            subscriptionId: alert.id,
            vendorName: alert.vendor_name,
          });
          return { success: true, skipped: true };
        }
        return { success: true, skipped: true };
      })
    );

    const successful = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    logger.info('Finished processing renewal alerts', {
      total: renewalAlerts.length,
      successful,
      failed,
    });

    return { successful, failed, total: renewalAlerts.length };
  }
}

export const subscriptionNotificationService = new SubscriptionNotificationService();
